/** Report type "Merged" (dati aggregati / merged crashes). */
export const STATS_REPORT_TYPE_MERGED = 0

/**
 * @param {{ id?: number, value?: number, description?: string, label?: string }} org
 * @returns {{ id: number, label: string }}
 */
function orgToReportTypeItem(org) {
  const id = Number(org.id ?? org.value)
  const label = org.description ?? org.label ?? String(id)
  return { id, label }
}

/**
 * Analyst di enti che non immettono dati (ministeri, ecc.): type 0 o 2, basic_organization -1.
 * Visualizzazione: tutte le org; statistiche: solo dataset Merged (reportType 0).
 * @param {object} [user]
 */
export function isNonInputAnalystUser(user) {
  if (!user) return false
  const basicOrg = Number(user.basic_organization)
  const type = Number(user.type)
  return basicOrg === -1 && (type === 0 || type === 2)
}

/**
 * Action Log / History sidebar: nascosta per analyst ministeriali (sola lettura aggregata).
 * @param {object} [user]
 */
export function canUserAccessActionHistory(user) {
  return !isNonInputAnalystUser(user)
}

/**
 * @param {object} [user]
 */
export function isSystemStatsAdmin(user) {
  if (!user) return false
  return Number(user.organization) === -1 || Number(user.type) === 1
}

/**
 * @param {object} [user]
 */
export function isMultiOrgStatsAdmin(user) {
  return Number(user?.type) === 3
}

/**
 * Report type accessibili per statistiche (Custom Chart / Dashboard).
 * Allineato alle regole di scope della Crash List.
 *
 * @param {object} params
 * @param {object} [params.user]
 * @param {Array<object>} [params.organizations]
 * @param {string} [params.mergedLabel]
 * @returns {Array<{ id: number, label: string }>}
 */
export function getAccessibleStatsReportTypes({
  user,
  organizations = [],
  mergedLabel = 'Merged',
}) {
  const allOrgs = organizations
    .filter((o) => Number(o.id ?? o.value) !== -1)
    .map(orgToReportTypeItem)

  const withMerged = (orgs) => [
    { id: STATS_REPORT_TYPE_MERGED, label: mergedLabel },
    ...orgs,
  ]

  if (isSystemStatsAdmin(user)) {
    return withMerged(allOrgs)
  }

  if (isMultiOrgStatsAdmin(user)) {
    const ids = Array.isArray(user.organization_ids)
      ? user.organization_ids.map(Number)
      : []
    const allowed = allOrgs.filter((o) => ids.includes(o.id))
    return withMerged(allowed)
  }

  if (isNonInputAnalystUser(user)) {
    return withMerged(allOrgs)
  }

  const userOrg = Number(user?.organization ?? 0)
  if (userOrg > 0) {
    const own = allOrgs.find((o) => o.id === userOrg)
    return own ? [own] : [{ id: userOrg, label: String(userOrg) }]
  }

  return withMerged(allOrgs)
}

/**
 * reportType di default per richieste Stats.
 * Analyst (ministry): sempre Merged; altri profili come prima.
 * @param {object} [user]
 * @returns {number}
 */
export function getDefaultStatsReportType(user) {
  if (!user) return STATS_REPORT_TYPE_MERGED
  if (isNonInputAnalystUser(user)) return STATS_REPORT_TYPE_MERGED
  if (isSystemStatsAdmin(user)) return STATS_REPORT_TYPE_MERGED
  if (isMultiOrgStatsAdmin(user)) return STATS_REPORT_TYPE_MERGED

  const userOrg = Number(user.organization ?? 0)
  return userOrg > 0 ? userOrg : STATS_REPORT_TYPE_MERGED
}

/**
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 */
export function canUserChangeStatsReportType(user, organizations = []) {
  return getAccessibleStatsReportTypes({ user, organizations }).length > 1
}

/**
 * Stats: reportType inviato al backend (legacy ministero gestito server-side).
 * @param {object} [user]
 * @param {number | null | undefined} selectedReportType
 * @param {Array<object>} [organizations]
 * @returns {number}
 */
export function resolveStatsReportTypeForRequest(
  user,
  selectedReportType,
  organizations = [],
) {
  const rt = Number(selectedReportType ?? STATS_REPORT_TYPE_MERGED)
  if (user && !isStatsReportTypeAllowed(rt, user, organizations)) {
    return getDefaultStatsReportType(user)
  }
  return rt
}

/**
 * Verifica che il reportType scelto sia tra quelli accessibili.
 * @param {number} reportType
 * @param {object} [user]
 * @param {Array<object>} [organizations]
 */
export function isStatsReportTypeAllowed(reportType, user, organizations = []) {
  const allowed = getAccessibleStatsReportTypes({ user, organizations })
  return allowed.some((item) => item.id === Number(reportType))
}
